import React, { useMemo } from "react";
import { formatDate, hashColor, initials } from "../model/suiviLivraisonFormat";
import { ehpadSummary, groupCommandeRowsByDestination, groupTraitementsByCommande, isNearScrollEnd, patientKey } from "../model/suiviLivraisonRules";
import { commandeBlockStyle, commandeSummaryStyle, listMoreStyle, patientButton } from "../styles/suiviLivraisonStyles";
import { DestinationCommandeBlock } from "./DestinationCommandeBlock";
import { EmptyState } from "./SuiviBadges";
import { ProductionComments } from "./SuiviComments";

// Suivi patient : liste des patients à gauche, commandes du patient sélectionné à droite, chargées au défilement.

const SCROLL_THRESHOLD = 80;

function onScrollEnd(callback) {
    return event => {
        if (isNearScrollEnd(event.currentTarget, SCROLL_THRESHOLD)) callback();
    };
}

export function PatientSuivi({ patients, traitements, busyId, onRetraiter, onViewPhoto }) {
    return (
        <div style={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "320px minmax(0, 1fr)", background: "#f8fafc" }}>
            <PatientSelectionPane {...patients} onScroll={onScrollEnd(patients.onLoadMore)} />
            <PatientDetailsPane
                {...traitements}
                busyId={busyId}
                onRetraiter={onRetraiter}
                onViewPhoto={onViewPhoto}
                onScroll={onScrollEnd(traitements.onLoadMore)}
            />
        </div>
    );
}

function PatientSelectionPane({ grouped, loading, selectedKey, loadingMore, hasMore, onSelect, onScroll }) {
    let content;
    if (loading) {
        content = <EmptyState>Chargement…</EmptyState>;
    } else if (grouped.length === 0) {
        content = <EmptyState>Aucun patient trouvé.</EmptyState>;
    } else {
        content = (
            <div style={{ padding: "10px" }}>
                {grouped.map(group => {
                    const patient = group[0];
                    return (
                        <PatientListButton
                            key={patientKey(patient)}
                            patient={patient}
                            count={group.length}
                            active={selectedKey === patientKey(patient)}
                            onClick={() => onSelect(patient)}
                        />
                    );
                })}
                {loadingMore && <div style={listMoreStyle}>Chargement…</div>}
                {!loadingMore && hasMore && <div style={listMoreStyle}>Faites défiler pour charger plus de patients.</div>}
            </div>
        );
    }

    return (
        <div onScroll={onScroll} style={{ borderRight: "1px solid #e2e8f0", overflow: "auto", background: "white" }}>
            {content}
        </div>
    );
}

function PatientListButton({ patient, count, active, onClick }) {
    const colors = hashColor(`${patient.patientNom}${patient.patientPrenom}`);
    return (
        <button onClick={onClick} style={patientButton(active)}>
            <span style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: colors.bg,
                color: colors.color,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
                fontWeight: 800,
                flexShrink: 0,
            }}>
                {initials(patient.patientNom, patient.patientPrenom)}
            </span>
            <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", color: "#0f172a", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {patient.patientPrenom} {patient.patientNom}
                </span>
                <span style={{ display: "block", color: "#94a3b8", fontSize: "11px" }}>
                    {count} produit{count > 1 ? "s" : ""}
                </span>
                <span style={{ display: "block", color: "#64748b", fontSize: "11px", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {ehpadSummary(patient)}
                </span>
            </span>
        </button>
    );
}

function PatientDetailsPane({ selectedPatient, items, loading, hasMore, busyId, onRetraiter, onViewPhoto, onScroll }) {
    const commandes = useMemo(() => groupTraitementsByCommande(items), [items]);
    let content;
    if (!selectedPatient) {
        content = <EmptyState>Sélectionnez un patient.</EmptyState>;
    } else if (loading && items.length === 0) {
        content = <EmptyState>Chargement…</EmptyState>;
    } else {
        content = (
            <div style={{ padding: "16px" }}>
                <PatientHeader patient={selectedPatient} count={items.length} />
                {items.length === 0 ? (
                    <EmptyState>Aucun traitement.</EmptyState>
                ) : (
                    commandes.map(commandeRows => (
                        <CommandeBlock
                            key={commandeRows[0].commandePatientId ?? commandeRows[0].commandeId}
                            rows={commandeRows}
                            busyId={busyId}
                            onRetraiter={onRetraiter}
                            onViewPhoto={onViewPhoto}
                        />
                    ))
                )}
                {loading && items.length > 0 && <EmptyState>Chargement des commandes suivantes…</EmptyState>}
                {!loading && hasMore && <div style={{ padding: "14px", textAlign: "center", color: "#94a3b8", fontSize: "12px" }}>Faites défiler pour charger les commandes suivantes.</div>}
            </div>
        );
    }
    return <div onScroll={onScroll} style={{ minWidth: 0, overflow: "auto" }}>{content}</div>;
}

function PatientHeader({ patient, count }) {
    return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: "12px" }}>
            <div>
                <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "15px" }}>
                    {patient.patientPrenom} {patient.patientNom}
                </div>
                <div style={{ color: "#64748b", fontSize: "12px" }}>
                    {ehpadSummary(patient)}
                    {" · "}
                    {count} traitement{count > 1 ? "s" : ""} chargé{count > 1 ? "s" : ""}
                </div>
            </div>
        </div>
    );
}

function CommandeBlock({ rows, busyId, onRetraiter, onViewPhoto }) {
    const commande = rows[0];
    return (
        <details open style={commandeBlockStyle}>
            <summary style={commandeSummaryStyle}>
                <span>
                    <strong style={{ color: "#0f172a" }}>Commande #{commande.commandeId}</strong>
                    <span style={{ color: "#64748b", marginLeft: "8px", fontSize: "12px" }}>{formatDate(commande.dateReception)}</span>
                </span>
                <span style={{ color: "#64748b", fontSize: "12px" }}>
                    {rows.length} produit{rows.length > 1 ? "s" : ""}
                </span>
            </summary>
            <div style={{ padding: "0 12px 12px" }}>
                <ProductionComments row={commande} />
                {groupCommandeRowsByDestination(rows).map(([destination, destinationRows]) => (
                    <DestinationCommandeBlock
                        key={destination}
                        destination={destination}
                        rows={destinationRows}
                        busyId={busyId}
                        onRetraiter={onRetraiter}
                        onViewPhoto={onViewPhoto}
                    />
                ))}
            </div>
        </details>
    );
}
